import * as THREE from 'three';
import { clamp } from '../core/math';
import { camera, hemi, rim, rim2, scene, setSunDir, sun } from '../render/scene';
import { TILE_M, asphaltTex, decalTex, farRuins, glowTex, lightTex, skyTex } from '../render/ground';
import { normalFrom, roughFrom } from '../render/tex-normal';
import { envMap } from '../render/env';
import { onQuality } from '../platform/quality';
import { STEP, blocksAt, clearRamps, groundYAt, reindex } from './terrain';
import { animBeams, animFires, buildField } from './maps/field';
export { STEP, VAULT, climbWaypoint, groundYAt, onRamp, ramps } from './terrain';
/* ============================================================
   關卡定義與地形
   ============================================================ */
/** 場地半徑。**68 → 150 → 240**（使用者 2026-08-03：「開放地圖，很大的地圖」，
    以及隔天的「直接照抄 world_sample」）。
    ⚠️ 放大之後構件數從 30 跳到近 3000，`pointBlocked`／`resolveCircle` 是
    每隻敵人每步都會呼叫的 —— 所以下面有空間索引，不能改回線性掃描。 */
/* **1 世界單位 = 1 公尺**（使用者 2026-08-03 拍板，跟著
   `docs/game-enjoy/09-level-design.md` 與 `world_sample.html` 的基準）。

   半徑 240 ＝ **480 公尺見方**，就是移植進來的那張圖（`world_sample.html`
   的 `MAP=480`）。使用者拍板「照抄、座標原封不動」，所以場地跟著它走。
   ⚠️ 代價是「橫越全圖約 40 秒」（文件 §54）變成約 80 秒 ——
   那條判準要重訂或接受它不成立，等實際玩過再說。 */
export var ARENA = 240;

/* ---------- 場地形狀：**方形**（使用者 2026-08-04 拍板）----------

   原本是圓盤（所有邊界判定都是 `hypot(x, z) < ARENA`）。改方形的理由是
   移植進來的那張圖是 480×480 的方城 —— 圓盤會把四個角切掉 49,400 m²（21%），
   而那四個角是原型畫過內容的地方。

   **所有邊界判定一律走下面兩支**，不要再自己寫 `hypot` 或 `Math.abs`：
   形狀分散在七個檔案裡的話，下次要再改就一定會漏掉一個，
   而漏掉的症狀是「某一種東西可以走出場外」——那不會有任何錯誤訊息。 */

/** 場地面積（`ARENA` 是**半邊長**，所以是 2×ARENA 見方）。 */
export function arenaArea() { return (ARENA * 2) * (ARENA * 2); }

/** 在場內嗎（留 `margin` 公尺的邊）。 */
export function inArena(x, z, margin?) {
  var lim = ARENA - (margin || 0);
  return Math.abs(x) <= lim && Math.abs(z) <= lim;
}

/* 共用的回傳物件。夾邊界是熱路徑（300 隻敵人每步各一次），
   每次配置一個新物件會讓 GC 在同屏峰值時抖動。 */
var _ac = { x: 0, z: 0 };
/** 夾進場內（方形）。回傳的是**共用物件**，拿了就用，不要存起來。 */
export function clampArena(x, z, margin?) {
  var lim = ARENA - (margin || 0);
  _ac.x = x < -lim ? -lim : (x > lim ? lim : x);
  _ac.z = z < -lim ? -lim : (z > lim ? lim : z);
  return _ac;
}

export var STAGES = [
  /* 第一階段的波次長度 55 → 42 秒。原本的 55 秒是在「每 15 秒升一級」的前提下訂的，
     M3 拔掉數值升級層之後第一階段完全沒有成長，要用初始數值硬撐 58 秒才等到 Boss ——
     實測機器人 56 秒就陣亡，等於看不到 Boss。 */
  /* `site` 是這一幕的**固定地標組**、`props` 是散佈的雜物種類
     （決定 3「每幕換版型」、決定 4「手作骨架＋程序填充」）。
     ⚠️ 2026-08-04 之後**地形欄位（terrain / site / props）已經沒有作用**：
     場地只有一種（空曠場地），四幕的差別在配色、霧與光影斑。
     欄位留著是因為圖鑑與存檔會讀 `name`／`bg`／`ac`。 */
  { name: '外圍機庫', en: 'OUTER HANGAR', bg: 0x090d16, g1: 0x1d2c45, g2: 0x2f5680, ac: 0x5fe3ff, terrain: 'pillars', site: 'ruins', props: ['vehicle', 'rubble', 'container'], waveDur: 42, boss: 'walze' },
  { name: '熔煉層', en: 'FOUNDRY', bg: 0x140a06, g1: 0x3a2415, g2: 0x6b3a18, ac: 0xff8a3d, terrain: 'blocks', site: 'foundry', props: ['pipes', 'container', 'vehicle'], waveDur: 65, boss: 'zenit' },
  { name: '冷卻塔', en: 'COOLANT SPIRE', bg: 0x061016, g1: 0x14303a, g2: 0x1f6070, ac: 0x7dffe8, terrain: 'spires', site: 'spire', props: ['container', 'rubble', 'vehicle'], waveDur: 75, boss: 'mutter' },
  { name: '核心', en: 'THE CORE', bg: 0x16060e, g1: 0x3a1424, g2: 0x6b1f3a, ac: 0xff2f6a, terrain: 'ruins', site: 'core', props: ['rubble', 'rubble', 'vehicle'], waveDur: 85, boss: 'kathedrale' }
];

/* `gridFine`／`gridCoarse` 是**刻意留成空的**（曾經是兩層 GridHelper）。

   使用者 2026-08-03：「我不要看起來像是訓練場」——等距發光格線是 debug
   場景的視覺簽名，蓋什麼建築上去都救不回來，所以整組拿掉。
   欄位保留是因為圖鑑與機庫會存／還原它們的可見性；改成 null 的話
   那兩個檔要一起改，而它們跟這一輪無關。 */
export var floorMesh, macroMesh, cloudMesh, skyMesh, dustMesh, edgeRing, farSkyline, farSkyline2, ashMesh;
export var gridFine: any = new THREE.Group(), gridCoarse: any = new THREE.Group();
export var terrainGroup = new THREE.Group(); terrainGroup.userData.terrain = true; scene.add(terrainGroup);
/* `userData.terrain` 只給驗收工具用（`tools/boss-shot.js` 要把地形整組關掉，
   否則造型截圖十張有三張被柱子擋住）。不參與任何遊戲邏輯。 */
export var obstacles = [];   // {kind:0 box|1 cyl, x,z,rot,hw,hd,r,br}

/** 近景餘燼的顆數與半盒邊長。**這是效能旋鈕之一**（一個 draw call，
    但 `animField` 每幀要走一次陣列）—— 420 顆在 SwiftShader 上量不到差異。 */
var ASH_N = 420, ASH_BOX = 45;

(function buildArenaBase() {
  /* 空曠場地的五層（`render/ground.ts` 有完整理由）：
     ① 柏油路面：接縫、裂縫、油漬、碎石，重複鋪滿場地
     ② 整場一張的貼花：彈坑、車轍、外圍壓暗（**乘算**）
     ③ 雲影：同樣乘算、緩慢飄移 —— 這是「光影」
     ④ 場外的遠景廢墟：地平線上有東西，邊界因此讀成「更遠的地方」
     ⑤ 灰燼天空球 */
  var tex = asphaltTex();
  if (tex) { tex.repeat.set((ARENA * 2) / TILE_M, (ARENA * 2) / TILE_M); }
  floorMesh = new THREE.Mesh(new THREE.PlaneGeometry(ARENA * 2.4, ARENA * 2.4),
    new THREE.MeshStandardMaterial({ color: 0x9aa6b8, map: tex as any, metalness: 0.03, roughness: 0.97 }));
  floorMesh.rotation.x = -Math.PI / 2; floorMesh.position.y = 0;
  floorMesh.receiveShadow = true;   // 只有地面接收陰影，機體投影（見 platform/renderer.ts）
  scene.add(floorMesh);

  /* 貼花與雲影都是**乘算**：白＝不變、暗＝髒／被雲擋住。
     加算會讓地面越疊越亮，最後變回一張灰紙 —— 而黃昏的地面該是暗的。 */
  var dt = decalTex();
  macroMesh = new THREE.Mesh(new THREE.PlaneGeometry(ARENA * 2, ARENA * 2),
    new THREE.MeshBasicMaterial({
      map: dt as any, transparent: true, depthWrite: false, fog: true,
      blending: THREE.MultiplyBlending, opacity: dt ? 1 : 0,
    }));
  macroMesh.rotation.x = -Math.PI / 2; macroMesh.position.y = 0.02;
  macroMesh.renderOrder = 1; scene.add(macroMesh);

  var lt = lightTex();
  if (lt) lt.repeat.set(1.4, 1.4);
  cloudMesh = new THREE.Mesh(new THREE.PlaneGeometry(ARENA * 2.4, ARENA * 2.4),
    new THREE.MeshBasicMaterial({
      map: lt as any, transparent: true, depthWrite: false, fog: true,
      blending: THREE.MultiplyBlending, opacity: lt ? 1 : 0,
    }));
  cloudMesh.rotation.x = -Math.PI / 2; cloudMesh.position.y = 0.04;
  cloudMesh.renderOrder = 2; scene.add(cloudMesh);

  /* 邊界不畫警示帶（使用者 2026-08-04：「看不到邊」）。欄位保留是因為
     圖鑑與機庫會存／還原它的可見性，改成 null 的話那兩個檔要一起改。 */
  edgeRing = new THREE.Group();

  /* 遠景兩層（大氣透視，見 `applyTheme`）：
     ① 1.08–3.0 倍半徑，看得出是建築  ② 3.2–7 倍半徑，只剩剪影
     第二層刻意矮一點（`hk` 0.72）：越遠的東西在畫面上越低，
     等高的話地平線會讀成一道等高的牆。 */
  /* `hk` 要小到讓剪影**留在地平線那條暖帶裡**。太高的話塔頂會頂進
     天頂那片近黑的藍，而它們自己是霧色（中灰）—— 中灰的方塊貼在近黑的
     天空上就是使用者看到的「一道白牆」，而且離真因（高度）非常遠。 */
  farSkyline = farRuins(ARENA, 0x141b26, { n: 130, d0: 1.06, d1: 0.9, hk: 0.30 });
  scene.add(farSkyline);
  farSkyline2 = farRuins(ARENA, 0x141b26, { n: 90, d0: 2.0, d1: 1.5, hk: 0.16, seed: 8686 });
  scene.add(farSkyline2);

  /* 空中的塵：黃昏逆光下唯一「空氣是髒的」的證據。
     2,600 顆點、一顆 draw call —— 空曠場地最便宜的氣氛。 */
  var da = new Float32Array(2600 * 3), ds = 20260804;
  function drnd() { ds = (ds * 1664525 + 1013904223) & 0x7fffffff; return ds / 0x7fffffff; }
  for (var di = 0; di < 2600; di++) {
    da[di * 3] = (drnd() * 2 - 1) * ARENA;
    da[di * 3 + 1] = drnd() * 62;
    da[di * 3 + 2] = (drnd() * 2 - 1) * ARENA;
  }
  var dg = new THREE.BufferGeometry();
  dg.setAttribute('position', new THREE.BufferAttribute(da, 3));
  dustMesh = new THREE.Points(dg, new THREE.PointsMaterial({
    /* ⚠️ 三件事都要做，少一件就讀成「下雪」：
       ① **一定要給 `map`** —— 沒有貼圖的 `PointsMaterial` 畫出來是
          **硬邊的正方形**，那是所有「畫面上有白色小方塊」回報的真因，
          而它看起來像 bug 不像塵。
       ② 顏色壓暗（`0xb0a290` 在 linear→sRGB 之後是 RGB(200) 的白）。
       ③ 不透明度壓到 0.14 —— 塵是「幾乎看不見但畫面有顆粒」。
       ④ 塵是**被月光照亮的**，所以它是冷的；暖色的塵會在畫面上變成
          一層黃紗，那是上一版整張圖偏黃的來源之一。 */
    map: glowTex() as any,
    size: 0.9, color: 0x565f6e, transparent: true, opacity: 0.14,
    depthWrite: false, sizeAttenuation: true,
  }));
  dustMesh.frustumCulled = false; scene.add(dustMesh);

  /* ---------- 近景的餘燼（`ASH_N` 顆，跟著玩家）----------

     上面那層塵鋪滿 480 m，而鏡頭一次只看到約 60 m —— 算下來畫面裡
     只剩十幾顆，等於沒有。**深度感來自近景**：在玩家周圍 ±45 m 的盒子裡
     再放一層更亮、更大、飄得更快的餘燼，走動時它們與地面產生視差，
     那是俯視遊戲少數幾個能讓畫面「有厚度」的手段，而且只要一個 draw call。

     ⚠️ 座標存的是**世界座標**，靠「離玩家太遠就繞回另一邊」跟隨。
     直接把整顆 Points 掛在玩家身上的話，餘燼會黏在鏡頭上跟著平移，
     視差歸零 —— 那正好是它存在的理由。 */
  var aa = new Float32Array(ASH_N * 3), as = 424242;
  function arnd() { as = (as * 1664525 + 1013904223) & 0x7fffffff; return as / 0x7fffffff; }
  for (var ai = 0; ai < ASH_N; ai++) {
    aa[ai * 3] = (arnd() * 2 - 1) * ASH_BOX;
    aa[ai * 3 + 1] = arnd() * 30;
    aa[ai * 3 + 2] = (arnd() * 2 - 1) * ASH_BOX;
  }
  var ag = new THREE.BufferGeometry();
  ag.setAttribute('position', new THREE.BufferAttribute(aa, 3));
  ashMesh = new THREE.Points(ag, new THREE.PointsMaterial({
    /* 加算混合：餘燼是**發亮的東西**，不是被照亮的東西。
       用一般混合的話它在暗背景上是灰點，讀成髒污。 */
    map: glowTex() as any,            // 見上面 dust 的第 ① 點：沒有 map 就是硬邊方塊
    size: 0.55, color: 0xff9a52, transparent: true, opacity: 0.55,
    blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
  }));
  ashMesh.frustumCulled = false; scene.add(ashMesh);

  var st = skyTex();
  skyMesh = new THREE.Mesh(new THREE.SphereGeometry(1900, 26, 18),
    new THREE.MeshBasicMaterial({ map: st as any, side: THREE.BackSide, fog: false, depthWrite: false }));
  skyMesh.renderOrder = -1; scene.add(skyMesh);
})();

/* ---------- 一關的色彩總譜（2026-08-04 改成冷夜鋼藍）----------

   使用者 2026-08-04：「場地配色（偏暖）跟遊戲調性不搭（偏冷，機甲風）」。
   前一輪照 `world_sample.html` 做的是**廢棄都市 · 黃昏**（暖褐主調），
   而 HUD、鎖定框、玩家的推進器與所有 UI 都是青（`#5fe3ff`）——
   畫面上兩套色系互不相干，戰鬥資訊浮在一張泛黃的照片上。

   現在的方向是**冷夜鋼藍**：月光式的冷白主光、藍灰的霧、
   深藍到青灰的天空。**整張畫面仍然只有三段顏色**，只是冷暖對調：

     冷底（約 70% 面積）：混凝土、柏油、霧、天空 … #6f7a8a → #1b1f2a（藍灰）
     冷光（約 20%）：月光直射面、鋼材、HUD ………… #c6d8ff → #5fe3ff
     暖點（約 10%，**只在真的在發光的東西上**）…… 火 #ff7326、窗燈 #ffd8a8、
                                                   餘燼 #ff9a52、爆炸與引擎焰

   ⚠️ **暖點不可以擴散成暖底**。上一版的問題就是把暖色鋪成底色（主光暖橘、
   地板染米色、柏油底是暖灰），於是整張圖沒有一處是冷的；反過來如果
   暖點也拿掉，畫面會變成單一色相的藍灰板 —— 對比來自「冷底 × 暖點」，
   不是把所有東西調成同一個色溫。

   ⚠️ 四幕仍然各有自己的 `ac`／`g1`／`g2`（圖鑑與存檔在讀），
   但**主光與霧不吃主題色**：入夜是這個世界的物理事實，
   不是關卡的配色選項。四幕的差別改由 `TINT` 表達（見下）。 */

/** 每一幕在「同一個夜晚」下的偏移：主光色溫、霧的濃度與色相、地面染色。
    幅度刻意很小 —— 四張圖要像同一個世界的四個角落，不是四張貼紙。 */
/** ⚠️ **這些 hex 是 linear 值，不是你在調色盤上看到的顏色。**
    專案鎖 three r128（沒有 `ColorManagement`），材質／霧／光的顏色一律被當成
    linear，最後由 `outputEncoding = sRGBEncoding` 提亮 —— 0.23 的 linear
    輸出成 0.51 的 sRGB，**幾乎是兩倍**。所以「看起來對」的值要填得比直覺暗。
    2026-08-04 實看抓到的：霧填 `0x3a352e`（自以為是深灰）之後，地平線變成
    一道 RGB(190) 的**白牆**，整個遠景的深度感歸零。 */
/** ⚠️ 換色時**保持每一欄的明度**（0.299R+0.587G+0.114B），只轉色相。
    黃昏那一版的亮度關係是實看調過六輪的（霧比天空亮、遠景比霧暗、
    地面壓得住威脅色）——把暖橘直接換成同名的藍會讓明度掉一截，
    症狀是「變冷了但整個畫面糊掉」，而真因是明度不是色相。 */
var TINT = [
  /* 外圍機庫：月光最乾淨的一幕，塵最少，看得最遠 */
  { sun: 0xc6d8ff, hemi: 0x8496b4, gnd: 0x1e2530, fog: 0x1b1f2a, dens: 0.0034, floor: 0xccd8ec, sky: 1.00 },
  /* 熔煉層：煙最重，底下的爐火把霧染出一點紫紅（暖點染霧，不是暖光照場） */
  { sun: 0xc9d2e8, hemi: 0x60607a, gnd: 0x2a2130, fog: 0x231d28, dens: 0.0044, floor: 0xbec6d8, sky: 0.92 },
  /* 冷卻塔：水氣最重，整幕往青偏（這一幕的辨識度＝冷得發青） */
  { sun: 0xd4e8ff, hemi: 0x74a0b0, gnd: 0x1e2c30, fog: 0x1c2a2e, dens: 0.0040, floor: 0xc4d4da, sky: 1.04 },
  /* 核心：最深的一幕，只剩核心自己的紅光染在霧上 */
  { sun: 0xb0bce0, hemi: 0x585a7e, gnd: 0x241a2a, fog: 0x1a1826, dens: 0.0048, floor: 0xbcc0d4, sky: 0.86 },
];

/* ---------- 地面的表面細節（2026-08-04 質感第二波）----------

   三件事共用同一張亮度圖（`render/tex-normal.ts` 從柏油貼圖算出來的）：

   ① **法線圖**：柏油的裂縫、接縫、碎石本來只是「畫上去的顏色」，
      掛上法線之後才會隨光的方向產生明暗 —— 近景看地面不再是一張紙。
   ② **粗糙度圖**：暗的地方（油漬、水痕、積水窪）粗糙度壓到 0.3、
      亮的乾燥處留在 1.0。
   ③ **環境反射**：讓 ② 的光滑處真的反射天空 ＝ **積水**。
      使用者選的是「局部積水」不是「整片濕」，而「暗的地方是濕的」
      正好是柏油上真實的分佈，不必另外畫一張遮罩。

   ⚠️ **只掛在地面與鋼材上，不設 `scene.environment`** —— 後者會把 IBL
   加到場上每一個標準材質（含敵人與機體），整體亮度跳一階，而冷夜那組
   配色是照現在這組光訂的（見 `render/env.ts` 檔頭）。

   ⚠️ 建立時要跑一次 1024² 的 Sobel（約 100 ms），所以
   **①②③ 全部歸畫質設定管**：低畫質時 `buildFloorDetail()` 根本不會被呼叫，
   不是算完再關掉 —— 低階機器連那 100 ms 的開場卡頓都不會有。 */
var _fNorm: any = null, _fRough: any = null, _fEnv: any = null, _fBuilt = false;
function buildFloorDetail() {
  if (_fBuilt || !floorMesh) return;
  var m: any = floorMesh.material;
  if (!m.map) return;                       // headless：沒有 canvas 就沒有貼圖
  _fBuilt = true;
  _fNorm = normalFrom(m.map, 1.15);
  if (_fNorm) _fNorm.repeat.copy(m.map.repeat);
  _fRough = roughFrom(m.map, 0.30, 1.0);
  if (_fRough) _fRough.repeat.copy(m.map.repeat);
  _fEnv = envMap();
}
/** 依畫質把上面三張圖掛上去／拿下來。**不重建**（重建一次 Sobel 要 100 ms，
    而且會拿到不同的隨機貼圖）—— 建好的存在模組變數裡反覆掛。 */
function applyFloorQuality(q) {
  if (!floorMesh) return;
  if (q.floorDetail) buildFloorDetail();
  var m: any = floorMesh.material;
  m.normalMap = q.floorDetail ? _fNorm : null;
  if (_fNorm) m.normalScale = new THREE.Vector2(0.75, 0.75);
  m.roughnessMap = q.floorDetail ? _fRough : null;
  /* 粗糙度純量在有貼圖時要放到 1.0（貼圖是乘上去的），沒貼圖時回到原本的 0.97。 */
  m.roughness = (q.floorDetail && _fRough) ? 1.0 : 0.97;
  /* `envMapIntensity` 壓到 0.30：這是**反射**不是補光。開到 1 的話
     地面會被天空的漫射照亮一階，畫面回到「灰藍色的板子」。 */
  m.envMap = (q.floorEnv && _fEnv) ? _fEnv : null;
  m.envMapIntensity = 0.30;
  m.needsUpdate = true;
}
var _qHooked = false;

export function applyTheme(S) {
  var idx = STAGES.indexOf(S); if (idx < 0) idx = 0;
  var T = TINT[idx];
  /* 畫質的訂閱**在這裡才註冊**：`onQuality` 註冊當下會立刻套用一次，
     而地面的細節要等 renderer 與環境圖都好了才建得起來 ——
     在模組載入時註冊的話那一次會白跑（而且會在 import 期間跑 Sobel）。 */
  if (!_qHooked) { _qHooked = true; onQuality(applyFloorQuality); }
  scene.background = new THREE.Color(T.fog).multiplyScalar(0.55);
  /* 霧：**空曠場地全靠它**。用 `FogExp2`（跟原型同一支）——
     線性霧在 480 m 見方的場地上會出現一條「霧開始了」的環，
     指數霧沒有那條線，所以邊界與遠景之間讀不出界線。

     ⚠️ 霧色**不可以比天空暗**。暗霧＝把遠處染黑，讀成「畫面外圍壞掉了」；
     亮一階的暖霧才讀成「空氣裡有塵」，而且它同時做掉大氣透視
     （遠的東西自動變淡變暖），那是深度感最便宜的來源。 */
  scene.fog = new THREE.FogExp2(T.fog, T.dens);
  /* 地板：柏油貼圖自己就夠暗，主題色只加色溫 ——
     直接吃主題色的話整片會是飽和的藍／橘，讀起來像一塊上了色的塑膠板。 */
  floorMesh.material.color.setHex(T.floor);
  /* 雲影往主題色偏一點，四幕的差別主要在這裡與霧色 */
  if (cloudMesh) cloudMesh.material.color.setHex(0xffffff).lerp(new THREE.Color(S.g2), 0.12);
  /* 遠景廢墟：**兩層**。近的一層偏暖偏亮（還看得出是建築），
     遠的一層幾乎融進霧裡 —— 兩層之間的明度差就是大氣透視，
     一層的話地平線讀成一道剪紙。 */
  if (farSkyline) {
    /* **比霧暗**才讀得出剪影。跟霧同色或更亮的話，遠景會跟霧融成一片
       均勻的板子（見 `TINT` 上面那段：這正是「白牆」的另一半原因）。 */
    var sc = farSkyline.userData.mat.color;
    sc.setHex(T.fog).multiplyScalar(0.42);
  }
  if (farSkyline2) {
    var sc2 = farSkyline2.userData.mat.color;
    sc2.setHex(T.fog).multiplyScalar(0.62);
  }
  if (skyMesh) skyMesh.material.color.setScalar(T.sky);
  /* **主光照原型的黃昏**：方位 2.42 rad、仰角 0.38 rad（22°）。
     俯視角唯一能講「這東西多高」的線索就是影子長度，仰角 22° →
     影長 ≈ 2.5 × 高，於是三階高度變成三種影子長度，不用圖例。
     ⚠️ 只搬強度不搬方向的話，遊戲原本那盞高角度的光會把它打成正午平光：
     建築沒有側面、地面過曝（`lessons.md` 2026-08-02 記過同型的坑）。 */
  setSunDir(Math.cos(2.42) * 200, Math.tan(0.38) * 200 + 20, Math.sin(2.42) * 200);
  /* 半球光：`sky` 是月光下的藍、`ground` 是**更深一階的冷色**。
     真實世界的陰影不是黑的，是被天空與地面反射填起來的 ——
     入夜之後兩邊都冷，立體感改由**明度差**來做（`sky` 比 `ground`
     亮三倍以上），不再靠色溫差。⚠️ 兩邊調成同一個亮度的話陰影會平掉，
     那是「調暗的灰藍場景」的第一個症狀。 */
  hemi.color.setHex(T.hemi); hemi.groundColor.setHex(T.gnd);
  sun.color.setHex(T.sun);
  /* **戰場偏暗**（使用者 2026-08-03：「畫面可以偏暗」），但**暗的是中性色，
     不是全部**：主光拉到 1.62（原型是 2.05）讓受光面真的亮起來，
     半球光留在 0.46 讓暗部沉下去 —— 對比來自兩者的差，不是把兩者一起壓低。
     ⚠️ 主光必須大於所有色光的總和，否則緣光會反過來變成主光把整個畫面
     染色（`lessons.md` 2026-08-02）。 */
  hemi.intensity = 0.54;
  sun.intensity = 1.68;
  rim.color.setHex(S.ac); rim.intensity = 0.26;
  rim2.intensity = 0.20;
  if (skyMesh) skyMesh.visible = true;
  /* 鏡頭遠平面：場地 480 m、遠景廢墟到 700 m、天空球 1900 m ——
     舊的 400 會把整片遠景切掉，畫面外圍變成一圈背景色。 */
  if (camera.far < 2200) { camera.far = 2200; camera.updateProjectionMatrix(); }
}

/* 光影 -------------------------------------------------------
   空曠場地唯一的動態：雲影緩慢掃過地面。**跑在畫面幀上**（純視覺）。 */
var _cloudT = 0;
export function animField(dt, px?, pz?) {
  _cloudT += dt;
  animFires(dt);
  animBeams(dt);
  if (ashMesh && px !== undefined) {
    /* 餘燼：往風向飄、緩緩沉，離玩家超過半盒就繞到對面（見建立處的註解）。
       上升的那幾顆是熱氣帶起來的 —— 全部往下掉會讀成雪。 */
    var ap = ashMesh.geometry.attributes.position.array as Float32Array;
    for (var i = 0; i < ap.length; i += 3) {
      var rise = (i % 9 === 0) ? 1 : -1;
      ap[i] += dt * 3.4; ap[i + 1] += dt * 1.1 * rise; ap[i + 2] += dt * 0.7;
      if (ap[i + 1] < 0.4) ap[i + 1] = 30; else if (ap[i + 1] > 30) ap[i + 1] = 0.4;
      var dx = ap[i] - px!; if (dx > ASH_BOX) ap[i] -= ASH_BOX * 2; else if (dx < -ASH_BOX) ap[i] += ASH_BOX * 2;
      var dz = ap[i + 2] - pz!; if (dz > ASH_BOX) ap[i + 2] -= ASH_BOX * 2; else if (dz < -ASH_BOX) ap[i + 2] += ASH_BOX * 2;
    }
    ashMesh.geometry.attributes.position.needsUpdate = true;
  }
  if (cloudMesh && cloudMesh.material.map) {
    /* 兩個軸用不同的速率 —— 同速率會讀成「整張圖在斜著滑」，
       不同速率才讀成「雲在飄」。週期長到看不出循環。 */
    cloudMesh.material.map.offset.set(_cloudT * 0.0026, _cloudT * 0.0014);
  }
  if (dustMesh) {
    /* 塵往同一個方向飄、越過邊界就回捲。**跟著玩家走**：
       場地 480 m 而鏡頭只看到 60 m，不回捲的話遠處的塵全部飄光了。 */
    var p = dustMesh.geometry.attributes.position.array as Float32Array;
    for (var i = 0; i < p.length; i += 3) {
      p[i] += dt * 2.6; p[i + 1] += dt * 0.35;
      if (p[i] > ARENA) p[i] = -ARENA;
      if (p[i + 1] > 62) p[i + 1] = 0;
    }
    dustMesh.geometry.attributes.position.needsUpdate = true;
  }
}

/* 構件的空間索引 -----------------------------------------------

   場地放大到半徑 150 之後，一張圖有 200+ 個構件，而
   `pointBlocked`／`resolveCircle` 是**每隻敵人每一步**都會呼叫的：
   300 隻 × 200 個 × 60 步 = 每秒 360 萬次 —— 線性掃描到這裡就爆了。

   做法是最簡單的均勻網格：每個構件插進它 AABB 覆蓋到的所有格子，
   查詢只看 3×3 鄰域。格子 24 單位、鄰域 72 單位，比任何單一構件都大。 */
var OCELL = 24;
var oidx = new Map<number, any[]>();
/** ⚠️ **鍵是整數、去重用物件欄位** —— 字串鍵與 WeakMap 在這條熱路徑上
    都會變成每秒數十萬次的配置（實測整個測試套件慢了三倍）。 */
function okey(i, j) { return (i + 2048) * 4096 + j + 2048; }
function oput(o) {
  var br = (o.kind === 1 ? o.r : Math.hypot(o.hw, o.hd)) + 2;
  var x0 = Math.floor((o.x - br) / OCELL), x1 = Math.floor((o.x + br) / OCELL);
  var z0 = Math.floor((o.z - br) / OCELL), z1 = Math.floor((o.z + br) / OCELL);
  for (var i = x0; i <= x1; i++) for (var j = z0; j <= z1; j++) {
    var k = okey(i, j);
    var a = oidx.get(k); if (!a) oidx.set(k, a = []);
    a.push(o);
  }
}
export function reindexObstacles() {
  oidx = new Map();
  for (var i = 0; i < obstacles.length; i++) oput(obstacles[i]);
}
/** (x,z) 附近可能碰得到的構件。 */
var _near: any[] = [], _stamp = 0;
export function obstaclesNear(x, z) {
  _near.length = 0; _stamp++;
  var ix = Math.floor(x / OCELL), iz = Math.floor(z / OCELL);
  for (var i = -1; i <= 1; i++) for (var j = -1; j <= 1; j++) {
    var c = oidx.get(okey(ix + i, iz + j)); if (!c) continue;
    for (var k = 0; k < c.length; k++) {
      var o: any = c[k];
      if (o._st === _stamp) continue;
      o._st = _stamp; _near.push(o);
    }
  }
  return _near;
}

export function addObstacle(o) {
  o.br = o.kind === 1 ? o.r : (o.hw + o.hd) * 0.55;
  /* `h` 是構件的**高度**，給 `pointBlocked`／`losBlocked` 的 `atY` 用，
     同時也是它**頂面的高度**：矮的（≤ `VAULT`）衝刺翻得過去，
     高的擋跑步也擋衝刺，而兩者的頂面都站得上去（決定 2、決定 5）。 */
  if (o.h === undefined) o.h = 99;
  /* `stand`：頂面站不站得住。預設站得住 —— 一根柱子頂端站得上人是
     飛行系統送的免費玩法，關掉才要理由（例如尖頂、斜面的裝飾件）。 */
  if (o.stand === undefined) o.stand = o.h < 90;
  obstacles.push(o);
  return o;
}

/** 蓋一張場地（`world/maps/field.ts`）。這裡只負責清乾淨、
    然後把高度場的索引重建起來。

    ⚠️ **`reindex` 一定要在最後**：高度場是靠索引回答「這裡多高」的，
    漏掉的話新蓋的階梯全部是空氣（走上去掉下來、飛上去穿過去）。 */
export function buildTerrain(S?, stageIdx?, site?) {
  while (terrainGroup.children.length) terrainGroup.remove(terrainGroup.children[0]);
  obstacles.length = 0;
  clearRamps();
  var r = buildField(terrainGroup, addObstacle, ARENA);
  reindex(obstacles);
  reindexObstacles();
  return r;
}

/* 可摧毀的掩體 ------------------------------------------------
   B2 加的。KATHEDRALE 大聖堂這一場教的就是**你躲的掩體會消失** ——
   而那要成立，掩體就得真的消失：從 `obstacles` 移除（碰撞與 LOS 同步失效）、
   從場景移除（畫面上不能還留著一根柱子）。

   ⚠️ **`obstacles` 是模擬層的資料**，移除順序會影響之後每一次
   `resolveCircle`／`losBlocked` 的迭代 —— 所以只准從**確定性的路徑**呼叫
   （Boss 的招式排程），不要接到任何吃牆鐘或畫面幀的東西上。 */

/** 摧毀一塊掩體。已經被摧毀過的重複呼叫是安全的（回 false）。 */
export function destroyObstacle(o) {
  /* 場地的量體全部是 `fixed`（`world/maps/field.ts`）：階梯是玩家腳下的地、
     純牆是合批進同一顆 mesh 的 —— 兩者都拆得掉碰撞、拆不掉畫面上那塊幾何。 */
  if (o.fixed) return false;
  var i = obstacles.indexOf(o);
  if (i < 0) return false;
  obstacles.splice(i, 1);
  if (o.g && o.g.parent) o.g.parent.remove(o.g);
  /* 高度場的索引一定要跟著更新，否則玩家會站在一塊已經被轟掉的平台上
     （KATHEDRALE 那一課「你躲的掩體會消失」現在連腳下的地也算）。 */
  if (o.stand) reindex(obstacles);
  reindexObstacles();
  return true;
}

/** 離某個點最近的掩體（`maxD` 之內）。KATHEDRALE 用它挑「玩家正在躲的那一塊」——
    隨機挑一塊的話，玩家不會把「掩體消失」跟自己的走位連起來。 */
export function coverNearest(x, z, maxD) {
  var best = null, bd = maxD * maxD;
  for (var i = 0; i < obstacles.length; i++) {
    var o = obstacles[i];
    if (o.fixed) continue;            // 高台不算「掩體」：轟掉它會把站在上面的人丟進洞裡
    var dx = o.x - x, dz = o.z - z, d = dx * dx + dz * dz;
    if (d < bd) { bd = d; best = o; }
  }
  return best;
}

/** 大型 Boss 走過去把構件碾掉。**地面 Boss 不吃地形碰撞**（牠們比構件大得多，
    被一根柱子擋住會讀成 bug），代價是要自己清出一條路。 */
export function crushObstacles(x, z, R) {
  var n = 0;
  for (var i = obstacles.length - 1; i >= 0; i--) {
    var o = obstacles[i];
    if (Math.hypot(o.x - x, o.z - z) < R + o.br && destroyObstacle(o)) n++;
  }
  return n;
}

/* 幾何查詢 ----------------------------------------------------

   `atY` 是**這個查詢發生在多高**（省略＝貼地）。高於構件本身就不擋 ——
   這一個參數同時做掉兩件事：子彈飛得過矮牆，以及**玩家飛起來就失去掩體**
   （規格：空中的代價不是扣血，是那層保護消失）。 */
export function pointBlocked(x, z, pad, atY?) {
  var list = obstaclesNear(x, z);
  for (var i = 0; i < list.length; i++) {
    var o = list[i];
    if (atY !== undefined && atY > o.h) continue;
    if (o.kind === 1) { if (Math.hypot(x - o.x, z - o.z) < o.r + pad) return true; }
    else {
      var c = Math.cos(o.rot), s = Math.sin(o.rot);
      var dx = x - o.x, dz = z - o.z;
      var lx = dx * c + dz * s, lz = -dx * s + dz * c;
      if (Math.abs(lx) < o.hw + pad && Math.abs(lz) < o.hd + pad) return true;
    }
  }
  return false;
}
export var _res = { x: 0, z: 0 };
/** 水平推擠。`fromY`＝這個東西站在多高、`climb`＝它爬得上多高的落差
    （走路 `STEP`、衝刺 `VAULT`）。兩個都省略＝什麼都擋（舊行為）。

    **「衝刺能過矮掩體、跑步會被擋」就是這一個判定吃不同的 `climb`**
    （決定 2）—— 兩套碰撞程式碼必然會漂移，一個參數不會。 */
export function resolveCircle(x, z, R, fromY?, climb?) {
  /* 候選只取一次（用進來的位置）。推擠最多把人移動 R + 構件半徑，
     而鄰域是 72 單位 —— 兩次 pass 之後仍然在同一組候選的涵蓋範圍內。 */
  var list = obstaclesNear(x, z).slice();
  for (var pass = 0; pass < 2; pass++) {
    for (var i = 0; i < list.length; i++) {
      var o = list[i];
      if (!blocksAt(o, fromY, climb)) continue;
      if (o.kind === 1) {
        var dx = x - o.x, dz = z - o.z, d = Math.hypot(dx, dz), min = o.r + R;
        if (d < min) { if (d < 0.0001) { dx = 1; dz = 0; d = 1; } x = o.x + dx / d * min; z = o.z + dz / d * min; }
      } else {
        var c = Math.cos(o.rot), s = Math.sin(o.rot);
        var ex = x - o.x, ez = z - o.z;
        var lx = ex * c + ez * s, lz = -ex * s + ez * c;
        var cx = clamp(lx, -o.hw, o.hw), cz = clamp(lz, -o.hd, o.hd);
        var ddx = lx - cx, ddz = lz - cz, dd = Math.hypot(ddx, ddz);
        if (dd < R) {
          if (dd < 0.0001) {
            var pX = o.hw - Math.abs(lx), pZ = o.hd - Math.abs(lz);
            if (pX < pZ) lx = (lx < 0 ? -1 : 1) * (o.hw + R); else lz = (lz < 0 ? -1 : 1) * (o.hd + R);
          } else { var k = (R - dd) / dd; lx += ddx * k; lz += ddz * k; }
          x = o.x + lx * c - lz * s; z = o.z + lx * s + lz * c;
        }
      }
    }
  }
  _res.x = x; _res.z = z; return _res;
}
/** 視線被擋了沒。**沿線段走格子**而不是掃全部構件 ——
    這一支是每隻敵人每一步都會問一次的（`sim/enemies.ts` 的掩體繞行），
    200 個構件 × 300 隻 × 60 步的線性掃描會直接吃掉整個幀預算。 */
export function losBlocked(ax, az, bx, bz, atY?) {
  var vx = bx - ax, vz = bz - az, len2 = vx * vx + vz * vz;
  var len = Math.sqrt(len2);
  var steps = Math.max(1, Math.ceil(len / (OCELL * 0.8)));
  _stamp++;
  for (var s = 0; s <= steps; s++) {
    var t0 = s / steps;
    var ix = Math.floor((ax + vx * t0) / OCELL), iz = Math.floor((az + vz * t0) / OCELL);
    for (var ci = -1; ci <= 1; ci++) for (var cj = -1; cj <= 1; cj++) {
      var cell = oidx.get(okey(ix + ci, iz + cj)); if (!cell) continue;
      for (var k = 0; k < cell.length; k++) {
        var o: any = cell[k];
        if (o._st === _stamp) continue;
        o._st = _stamp;
        if (atY !== undefined && atY > o.h) continue;
        var t = len2 < 0.0001 ? 0 : clamp(((o.x - ax) * vx + (o.z - az) * vz) / len2, 0, 1);
        var px = ax + vx * t, pz = az + vz * t;
        if (Math.hypot(o.x - px, o.z - pz) < o.br) return true;
      }
    }
  }
  return false;
}
export function safeSpot(x, z, R?) {
  var g = groundYAt(x, z);
  if (!pointBlocked(x, z, R, g + STEP)) return { x: x, z: z };
  var r = resolveCircle(x, z, R, g, STEP);
  return { x: r.x, z: r.z };
}
